import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UNIFIED_THEME } from '../../styles/unifiedTheme';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ToastContainer';
import { getErrorMessage } from '../../utils/errorHandler';

export default function AddCity({ theme = UNIFIED_THEME }) {
  const toast = useToast();
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState('');
  const [cityName, setCityName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please login again.');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/countries`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch countries: ${response.status}`);
      }

      const data = await response.json();
      const countriesData = data.countries || data;

      if (Array.isArray(countriesData)) {
        setCountries(countriesData);
      } else {
        toast.error('Invalid countries data format');
      }
    } catch (error) {
      console.error('Fetch countries error:', error);
      const errorMsg = getErrorMessage(error, 'Failed to load countries');
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!countryId || !cityName.trim()) {
      toast.error('Please select a country and enter a city name');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = { country_id: countryId, city_name: cityName.trim() };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/new-city`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        toast.success(responseData.message || 'City added successfully!');
        setCityName('');
        setCountryId('');
      } else {
        const errorMsg = getErrorMessage(responseData.message || 'Failed to add city');
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMsg = getErrorMessage(error, 'Failed to add city');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer toasts={toast.toasts} onRemove={toast.remove} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white shadow-sm rounded-xl p-4 sm:p-6 w-full max-w-md mx-auto mt-4 sm:mt-6 border border-gray-200"
      >

      <h2 className="text-lg sm:text-xl font-semibold mb-4 text-center text-gray-800">
        Add New City
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Country</label>
          <select
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            disabled={isLoading}
            className="w-full p-2.5 text-sm border rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400/50 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            style={{
              borderColor: countryId ? theme.primary.main : '#d1d5db',
            }}
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.display_name || country.name || 'Unknown Country'}
              </option>
            ))}
          </select>
          {isLoading && <p className="text-xs text-gray-500 mt-1.5">Loading countries...</p>}
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">City Name</label>
          <input
            type="text"
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
            placeholder="Enter city name"
            className="w-full p-2.5 text-sm border rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400/50 focus:border-blue-500 transition disabled:bg-gray-100"
            style={{
              borderColor: cityName ? theme.primary.main : '#d1d5db',
            }}
            disabled={isSubmitting}
            minLength={2}
            maxLength={100}
            required
          />
        </div>

        <motion.button
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: !(!countryId || !cityName.trim() || isSubmitting) ? 1.01 : 1 }}
          type="submit"
          disabled={!countryId || !cityName.trim() || isSubmitting}
          className="w-full py-2.5 text-sm rounded-lg font-medium transition text-white shadow-sm"
          style={{
            backgroundColor: isSubmitting ? '#9ca3af' : theme.primary.main,
            opacity: (!countryId || !cityName.trim() || isSubmitting) ? 0.7 : 1,
            cursor: !countryId || !cityName.trim() || isSubmitting ? 'not-allowed' : 'pointer',
          }}
        >
          {isSubmitting ? 'Adding City...' : 'Add City'}
        </motion.button>
      </form>
    </motion.div>
    </>
  );
}